import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import dayjs from 'dayjs';
import { useGetCampaign } from '@frontend/api-bulk-messaging';
import { useHasBulkEmailAccess, useHasBulkTextAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { BulkMessageEditorProvider } from '../bulk-message-editor-provider';
import { BulkMessageEditorPage } from '../components/bulk-message-editor-page';
import { BulkMessageError } from '../components/bulk-message-error';
import { BulkMessageLoader } from '../components/bulk-message-loader';
import { BulkMessagePage } from '../components/bulk-message-page';
import { EmptyStateNoAccess } from '../components/empty-state-no-access';
import { BACKEND_DATE_TIME_FORMAT } from '../constants';
import { useCampaignSave } from '../hooks/use-campaign-save';
import { EditorView } from '../types';

type Props = {
  campaignId: string;
  campaignType: CampaignType;
  view: EditorView;
};

export const CampaignEdit = ({ campaignId, campaignType, view }: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { selectedOrgId: orgId } = useAppScopeStore();
  const { hasBulkEmailAccess } = useHasBulkEmailAccess();
  const { hasBulkTextAccess } = useHasBulkTextAccess();
  const hasAccess = campaignType === CampaignType.EMAIL ? hasBulkEmailAccess : hasBulkTextAccess;
  const { data: campaign, isLoading, isError } = useGetCampaign({ campaignId, orgId }, { enabled: hasAccess });
  const { lastSavedCampaign, ...editorActions } = useCampaignSave(campaignType, campaign);

  if (!hasAccess) {
    return <EmptyStateNoAccess campaignType={campaignType} />;
  }

  // The lastSavedCampaign is undefined while it updates in useBulkEmailEditorActions
  if (isLoading || !lastSavedCampaign) {
    return <BulkMessageLoader />;
  }

  // TODO: Show a specific 404 error message for a not found campaign instead of a generic error message
  if (isError || (campaign && campaign.type !== campaignType)) {
    const error =
      campaignType === CampaignType.EMAIL
        ? t('An error occurred while loading your email campaign.')
        : t('An error occurred while loading your text campaign.');
    return (
      <BulkMessagePage error={error}>
        <BulkMessageError heading={error} />
      </BulkMessagePage>
    );
  }

  return (
    <BulkMessageEditorProvider
      {...editorActions}
      campaignType={campaignType}
      initialCampaign={
        lastSavedCampaign
          ? {
              ...lastSavedCampaign,
              startSendAt: normalizeStartSendAt(lastSavedCampaign.startSendAt),
            }
          : undefined
      }
      view={view}
    >
      <BulkMessageEditorPage />
    </BulkMessageEditorProvider>
  );
};

// Remove seconds from the startSendAt date time. The backend stores date times with seconds, but the frontend doesn't support second-level scheduling.
// This avoids issues with the changes exists logic thinking there are unsaved changes due to second-level differences.
// TODO: Move this into its own util and unit test it!
const normalizeStartSendAt = (startSendAt: string) => {
  return startSendAt ? dayjs(startSendAt).second(0).format(BACKEND_DATE_TIME_FORMAT) : '';
};
